import { LoaderCircle, Printer, X } from 'lucide-react'
import type { CashClosingRecord } from '../../types'
import { formatMoney } from '../../lib/format'
import { formatReceiptDate } from '../../features/local-printing/services/receiptFormatters'
import { AppModal, Button } from '../ui'

const statusLabels: Record<CashClosingRecord['printStatus'], string> = {
  not_requested: 'No solicitada', pending: 'Pendiente', printed: 'Impresa', failed: 'Error', unknown: 'Desconocida',
}

export function CashClosingsHistoryModal({ canReprint, closings, printingClosingId, onClose, onReprint }: {
  canReprint: boolean
  closings: CashClosingRecord[]
  printingClosingId: string | null
  onClose: () => void
  onReprint: (closing: CashClosingRecord, confirmedNotPrinted?: boolean) => void
}) {
  return <AppModal containerClassName="!p-4" maxWidth={896} dismissDisabled={Boolean(printingClosingId)} label="Histórico de cierres" onClose={onClose}>
    <section className="flex max-h-[calc(100svh-32px)] w-full flex-col rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--surface)] shadow-[var(--shadow)]">
      <div className="flex items-start justify-between gap-4 border-b border-[var(--separator)] p-5">
        <div><h2 className="text-2xl font-black">Histórico de cierres</h2><p className="text-sm text-[var(--muted)]">Las copias se generan desde la instantánea guardada al cerrar.</p></div>
        <Button disabled={Boolean(printingClosingId)} className="!size-11 !min-h-11 !min-w-11 !shrink-0 !rounded-[12px] !p-0" aria-label="Cerrar" onClick={onClose} size="sm" type="button" variant="tertiary"><X className="h-4 w-4" /></Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] p-5">
        <div className="grid gap-3">
          {closings.map((closing) => {
            const printing = printingClosingId === closing.id
            return <article className="rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--background)] p-4" key={closing.id}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-black">{closing.printSnapshot.reportTitle} · {closing.printSnapshot.registerName}</p>
                  <p className="text-sm text-[var(--muted)]">{formatReceiptDate(closing.closedAt, closing.printSnapshot.timezone)}</p>
                  <p className="mt-1 font-mono text-xl font-black">{formatMoney(closing.printSnapshot.summary.totalSalesCents)}</p>
                  <p className="text-xs font-bold text-[var(--muted)]">{closing.printSnapshot.summary.salesCount} ventas · Impresión: {statusLabels[closing.printStatus]} · {closing.printCopies} copias</p>
                </div>
                <Button disabled={!canReprint || Boolean(printingClosingId)} onClick={() => onReprint(closing, closing.printStatus === 'unknown')} type="button" variant="secondary">
                  {printing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                  {printing ? 'Imprimiendo...' : closing.printStatus === 'unknown' ? 'He comprobado que no se imprimió · Crear copia' : 'Imprimir cierre'}
                </Button>
              </div>
              {closing.printStatus === 'unknown' ? <p className="mt-3 text-sm font-semibold text-amber-700">Comprueba físicamente que no salió papel antes de pulsar. El agente verificará que el trabajo anterior haya terminado.</p> : null}
            </article>
          })}
          {!closings.length ? <p className="rounded-[var(--radius)] border border-dashed border-[var(--separator)] p-8 text-center text-sm font-semibold text-[var(--muted)]">No hay cierres guardados.</p> : null}
        </div>
      </div>
    </section>
  </AppModal>
}
